import { isPlatformServer } from '@angular/common';
import { ErrorHandler, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { AndroidPermissions } from '@awesome-cordova-plugins/android-permissions/ngx';
import type { FileEntry, IFile } from '@awesome-cordova-plugins/file/ngx';
import { File } from '@awesome-cordova-plugins/file/ngx';
import type { FileTransferObject } from '@awesome-cordova-plugins/file-transfer/ngx';
import { FileTransfer } from '@awesome-cordova-plugins/file-transfer/ngx';
import { Capacitor } from '@capacitor/core';
import { Auth } from '@freelancer/auth';
import {
  MEDIA_STORE_DOWNLOADS_PLUGIN_NAME,
  mediaStoreDownloadsPlugin,
} from '@freelancer/media-store-downloads';
import { Pwa } from '@freelancer/pwa';
import { ErrorTracking } from '@freelancer/tracking';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { Observable } from 'rxjs';
import { BehaviorSubject, firstValueFrom, ReplaySubject } from 'rxjs';

export interface DownloadTask {
  readonly id: string;
  readonly name: string;
  readonly changes$: Observable<FileDownloadEvent>;
  readonly mimeType$: Observable<string>;
  cancel(): void;
}

export type FileDownloadEvent = {
  totalBytes?: number;
  transferedBytes?: number;
  /** Progress percentage (0-100) */
  progressPercentage?: number;
} & (
  | {
      status: 'running';
    }
  | {
      status: 'success';
      fileUrl: string;
    }
  | {
      status: 'error';
      errorCode:
        | FileDownloadError.UNKNOWN
        | FileDownloadError.PERMISSION_NOT_GRANTED;
    }
);

export enum FileDownloadError {
  UNKNOWN = 'file_download_unknown',
  PERMISSION_NOT_GRANTED = 'file_download_permission_not_granted',
}

/**
 * Subset of the non-standard Network Information API used for download-failure
 * triage. Absent on iOS WebKit, so all fields are optional.
 */
interface NetworkInformation {
  readonly effectiveType?: string;
  readonly type?: string;
  readonly downlink?: number;
  readonly rtt?: number;
}

interface NetworkDiagnostics {
  online: boolean;
  effectiveType?: string;
  connectionType?: string;
  downlinkMbps?: number;
  rttMs?: number;
}

/**
 * Wraps a native FileTransfer/Cordova failure in a real Error subclass so
 * Sentry titles the issue `FileDownloadException: …` (with the failing step)
 * instead of falling back to "Non-Error exception captured".
 */
class FileDownloadException extends Error {
  override name = 'FileDownloadException';
  constructor(step: string, cause: unknown) {
    const err = cause as
      | {
          code?: unknown;
          http_status?: unknown;
          message?: unknown;
        }
      | null
      | undefined;
    const parts = [`step="${step}"`];
    if (err?.code !== undefined) {
      parts.push(`code=${String(err.code)}`);
    }
    if (err?.http_status !== undefined) {
      parts.push(`http_status=${String(err.http_status)}`);
    }
    if (typeof err?.message === 'string' && err.message.length > 0) {
      parts.push(`message=${err.message}`);
    }
    super(`FileDownload failed (${parts.join(', ')})`, { cause });
  }
}

/**
 * Service to download a file
 *
 * @remarks
 *
 * If this service does not trigger the file to download but instead displays it
 * in the browser, this is because the `Content-Disposition` header is wrong
 * on the backend response.
 *
 * WARNING: do not use `window.open` as a workaround, as it would break
 * PWA/TWA/Native mobile support.
 *
 * @export
 */
@UntilDestroy({ className: 'FileDownload' })
@Injectable({
  providedIn: 'root',
})
export class FileDownload {
  private platformId = inject(PLATFORM_ID);
  private androidPermissions = inject(AndroidPermissions);
  private auth = inject(Auth);
  private file = inject(File);
  private pwa = inject(Pwa);
  private transfer = inject(FileTransfer);
  private errorHandler = inject(ErrorHandler);
  private errorTracking = inject(ErrorTracking);

  private downloadQueueSubject$ = new ReplaySubject<DownloadTask>(1);

  /**
   * Returns an observable that emits the created DownloadTask
   * every time a user triggers a file download.
   */
  getDownloadTaskStream(): Observable<DownloadTask> {
    return this.downloadQueueSubject$.asObservable();
  }

  /**
   * Best-effort network diagnostics for download-failure triage. The Network
   * Information API is non-standard and absent on iOS WebKit, so every field
   * other than `online` is optional.
   */
  private getNetworkDiagnostics(): NetworkDiagnostics {
    const { connection } = navigator as Navigator & {
      connection?: NetworkInformation;
    };
    return {
      online: navigator.onLine,
      effectiveType: connection?.effectiveType,
      connectionType: connection?.type,
      downlinkMbps: connection?.downlink,
      rttMs: connection?.rtt,
    };
  }

  /**
   * Download a file from a given URL
   */
  download(url: string, name: string): void {
    if (isPlatformServer(this.platformId)) {
      throw new Error(
        'Cannot trigger file download on page rendering. Please check code logic.',
      );
    }
    if (!url.startsWith('http')) {
      // TODO: T225788 throw on invalid URLs once existing violations are fixed.
      this.errorHandler.handleError(
        new Error(`Invalid non-absolute download URL "${url}"`),
      );
    }

    if (!this.pwa.isNative()) {
      // In browsers: trigger download and rely on browser UX
      window.location.href = url;
      return;
    }
    // In apps: manually download using FileTransfer

    // Create a stream to emit all the FileTransfer progress events
    const subject$ = new BehaviorSubject<FileDownloadEvent>({
      status: 'running',
    });
    let fileTransfer: FileTransferObject;
    const mimeType$ = new ReplaySubject<string>(1);
    // Captured for download-failure triage (see reportError below): how far the
    // transfer got, how long it ran, and where it was downloading from.
    const startedAt = Date.now();
    const urlHost = ((): string | undefined => {
      try {
        return new URL(url).host;
      } catch {
        return undefined;
      }
    })();
    let lastLoaded = 0;
    let lastTotal = 0;
    // Track which plugin call is in-flight so the error reporter can pinpoint
    // which Cordova/Capacitor call returns UNIMPLEMENTED on a given device.
    let step:
      | 'os_version_check'
      | 'android_permission_check'
      | 'android_permission_request'
      | 'resolve_destination'
      | 'transfer_create'
      | 'transfer_on_progress'
      | 'auth_header'
      | 'transfer_download'
      | 'transfer_file_metadata'
      | 'media_store_save' = 'os_version_check';
    const reportError = (error: unknown): void => {
      console.error(`[FileDownload] failed at step="${step}"`, error);
      const err = error as
        | {
            code?: unknown;
            http_status?: unknown;
            source?: unknown;
            target?: unknown;
          }
        | null
        | undefined;
      const network = this.getNetworkDiagnostics();
      this.errorTracking.captureException(
        new FileDownloadException(step, error),
        {
          step,
          fileName: name,
          fileExtension: name.includes('.') ? name.split('.').pop() : undefined,
          urlHost,
          platform: this.pwa.getPlatform(),
          isNative: this.pwa.isNative(),
          transferErrorCode: err?.code,
          httpStatus: err?.http_status,
          source: err?.source,
          target: err?.target,
          // Triage signal: connectivity vs server vs timeout. ABORT_ERR
          // (code=4) with http_status=null + online=false/poor connection
          // points to a dropped connection rather than a backend fault.
          online: network.online,
          effectiveConnectionType: network.effectiveType,
          connectionType: network.connectionType,
          downlinkMbps: network.downlinkMbps,
          rttMs: network.rttMs,
          // How far the transfer got before failing: 0 = never connected,
          // partial = mid-transfer drop, ~100 = failed in a post-download step.
          bytesTransferred: lastLoaded,
          totalBytes: lastTotal > 0 ? lastTotal : undefined,
          progressPercent:
            lastTotal > 0 ? Math.round((100 * lastLoaded) / lastTotal) : 0,
          durationMs: Date.now() - startedAt,
        },
      );
      subject$.next({
        status: 'error',
        errorCode: FileDownloadError.UNKNOWN,
      });
      subject$.complete();
    };
    (async () => {
      try {
        // Breadcrumb so a later failure shows when the download was triggered.
        this.errorTracking.addSentryBreadCrumb({
          category: 'file-download',
          message: 'File download started',
          level: 'info',
          data: { fileName: name, urlHost, isNative: this.pwa.isNative() },
        });
        if (this.pwa.getPlatform() === 'android') {
          // Do not need to require WRITE_EXTERNAL_STORAGE in Android 13+ (SDK 33)
          // Require it will cause an exception
          step = 'os_version_check';
          const doNotNeedPermission =
            await this.pwa.hasMinimumNativeOsMajorVersion(13);
          if (!doNotNeedPermission) {
            step = 'android_permission_check';
            const { hasPermission } =
              await this.androidPermissions.checkPermission(
                this.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE,
              );
            if (!hasPermission) {
              step = 'android_permission_request';
              const { hasPermission: hasAcceptedPermission } =
                await this.androidPermissions.requestPermission(
                  this.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE,
                );
              if (!hasAcceptedPermission) {
                subject$.next({
                  status: 'error',
                  errorCode: FileDownloadError.PERMISSION_NOT_GRANTED,
                });
                subject$.complete();
                return;
              }
            }
          }
        }

        step = 'resolve_destination';
        // Fall back to the legacy public-Downloads write if the
        // running an old version of the app. See D226932.
        const isAndroid = this.pwa.getPlatform() === 'android';
        const useMediaStore =
          isAndroid &&
          Capacitor.isPluginAvailable(MEDIA_STORE_DOWNLOADS_PLUGIN_NAME);
        // On Android use the cache dir, not the files dir:
        // the persistent copy lives in Downloads, and this working copy (kept
        // so the file can be opened via FileProvider) is then OS-reclaimable
        // rather than growing app storage unboundedly. See D226932.
        const destinationUrl = ((): string => {
          if (!isAndroid) {
            return this.file.dataDirectory;
          }
          return useMediaStore
            ? this.file.externalCacheDirectory
            : `${this.file.externalRootDirectory}/Download/`;
        })();

        step = 'transfer_create';
        fileTransfer = this.transfer.create();

        step = 'transfer_on_progress';
        fileTransfer.onProgress(event => {
          lastLoaded = event.loaded;
          lastTotal = event.total;
          subject$.next({
            status: 'running',
            progressPercentage: event.total
              ? 100 * (event.loaded / event.total)
              : 0,
            transferedBytes: event.loaded,
            totalBytes: event.total,
          });
        });

        step = 'auth_header';
        // FIXME: T267853 - some file download endpoints require authentication
        // this may not work properly if we switch to DownloadManager or similar
        const header = await firstValueFrom(
          this.auth.getAuthorizationHeader().pipe(untilDestroyed(this)),
        );

        step = 'transfer_download';
        const entry: FileEntry = await fileTransfer.download(
          url,
          `${destinationUrl}${name}`,
          false, // trustAllHosts
          {
            // convert HttpHeader back into object
            headers: Object.fromEntries(
              header.keys().map(key => [key, header.get(key)]),
            ),
          },
        );

        step = 'transfer_file_metadata';
        // MIME type for the MediaStore copy and the alert. Tolerate failures.
        const fileType = await new Promise<string>(resolve => {
          entry.file(
            (file: IFile) => resolve(file.type),
            () => resolve(''),
          );
        });
        mimeType$.next(fileType);

        // On Android, copy the app-scoped file into public Downloads so it's
        // user-visible. See D226932.
        const fileUrl = entry.toURL();
        if (useMediaStore) {
          step = 'media_store_save';
          await mediaStoreDownloadsPlugin.saveToDownloads({
            path: fileUrl,
            fileName: name,
            mimeType: fileType || undefined,
          });
        }

        subject$.next({
          status: 'success',
          fileUrl,
        });
        subject$.complete();
      } catch (error) {
        // TODO: T267853 - show file download error UI state
        reportError(error);
      }
    })();
    this.downloadQueueSubject$.next({
      id: `${name}${Math.random().toFixed(10)}`,
      name,
      changes$: subject$.asObservable(),
      mimeType$: mimeType$.asObservable(),
      cancel: () => fileTransfer.abort(),
    });
  }

  /**
   * Download a blob, and rename with the given filename
   *
   * @privateRemarks
   *
   * For IE browsers, blobs are saved using the msSaveBlob method, whilst other
   * browsers will be saved by creating a blob, then creating an invisible
   * anchor, and then triggering a download with the given filename.
   */
  saveBlob(blob: Blob, filename: string): void {
    if (window.navigator.msSaveBlob) {
      window.navigator.msSaveBlob(blob, filename);
    } else {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
    }
  }
}
